import type { TopicClassification } from '../../domain/topicClassification';
import type { TopicSource } from '../../domain/types/contentTypes';
import type { BrowseCoverBadge } from '../../domain/topicCoverBadge';
import { escapeHtml } from './formatUtils';
import { buildTopicCard, type TopicCardInput } from './topicCard';
import { canShowViewTopic, displayStatus, isVotable } from './communityIdeaPresentation';

export interface CommunityListItem {
	id: string;
	title: string;
	subtitle?: string | null;
	description?: string | null;
	status: string;
	isAdopted?: boolean;
	viewerLiked?: boolean;
	linksCount?: number;
	attachmentsCount?: number;
	navigableTopicSlug?: string | null;
	createdAt: string;
	category?: string | null;
	topicTitle?: string | null;
	topicSubtitle?: string | null;
	topicSummary?: string | null;
	topicCreatedAt?: string | null;
	topicChapterCount?: number | null;
	topicAudioMinutes?: number | null;
	topicClassification?: TopicClassification | null;
	topicCoverImageUrl?: string | null;
	topicSource?: TopicSource | null;
	coverBadge?: BrowseCoverBadge;
	isFavorite?: boolean;
	voteValue?: -1 | 0 | 1;
}

const thumb =
	'<svg aria-hidden="true" data-icon-source="lucide" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 10v12"></path><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"></path></svg>';
const sparkle =
	'<svg aria-hidden="true" data-icon-source="lucide" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 3-1.5 5.5L5 10l5.5 1.5L12 17l1.5-5.5L19 10l-5.5-1.5Z"></path></svg>';
const link =
	'<svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>';
const attachment =
	'<svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path></svg>';

export function renderCommunityIdeaListItem(idea: CommunityListItem): HTMLElement {
	const selected = !isVotable(idea);
	const status = displayStatus(idea);
	const subtitle = idea.subtitle?.trim() || idea.description?.trim() || '';
	const liked = Boolean(idea.viewerLiked);
	const hasTopic = canShowViewTopic(idea);
	const support = !selected
		? `<div class="idea-action community-support-action"><span class="community-support"><button type="button" class="vote-btn ${liked ? 'voted' : ''}" data-idea-id="${escapeHtml(idea.id)}" data-proto-click="voteIdea('${escapeHtml(idea.id)}', this)" aria-label="${liked ? 'You supported this request' : 'Support this request'}">${thumb}<span class="community-support-sparkles">${sparkle.repeat(7)}</span></button></span></div>`
		: '';
	const topic = hasTopic
		? `<div class="idea-action community-topic-action"><button type="button" class="btn btn-outline community-view-topic" data-proto-click="showTopicDetail('${escapeHtml(idea.navigableTopicSlug!.trim())}')">View Topic</button></div>`
		: '';
	const attachments = Number(idea.attachmentsCount) || 0;
	const links = Number(idea.linksCount) || 0;
	const template = document.createElement('template');

	template.innerHTML = `<article class="idea-item" tabindex="-1" data-status="${status.toLowerCase()}" data-has-subtitle="${String(Boolean(subtitle))}" data-has-topic="${String(hasTopic)}" data-has-vote-action="${String(!selected)}" data-is-adopted="${idea.isAdopted ? 'true' : 'false'}" data-viewer-liked="${String(liked)}" data-idea-id="${escapeHtml(idea.id)}" data-proto-click="openCommunityIdeaDialog('${escapeHtml(idea.id)}')"><div class="idea-content"><h4><span class="idea-item-title-action" role="button" tabindex="0" data-keyboard-action="true" data-proto-click="openCommunityIdeaDialog('${escapeHtml(idea.id)}')">${escapeHtml(idea.title)}</span></h4>${subtitle ? `<p>${escapeHtml(subtitle)}</p>` : ''}<div class="idea-footer"><span class="pill ${status === 'Selected' ? 'pill-completed' : 'pill-adopted'}">${status}</span><span>${attachment}${attachments} attachment${attachments === 1 ? '' : 's'}</span><span>${link}${links} link${links === 1 ? '' : 's'}</span></div></div>${support}${topic}</article>`;

	return template.content.firstElementChild as HTMLElement;
}

export function renderCommunityBuiltTopicCard(idea: CommunityListItem): HTMLElement {
	const slug = idea.navigableTopicSlug?.trim();
	if (!slug) return document.createElement('div');

	return buildTopicCard(
		{
			slug,
			title: idea.topicTitle ?? idea.title,
			source: idea.topicSource ?? undefined,
			coverBadge: idea.coverBadge,
			subtitle: idea.topicSubtitle ?? idea.topicSummary ?? undefined,
			description: idea.topicSummary ?? undefined,
			category: idea.category,
			classification: idea.topicClassification,
			coverImageUrl: idea.topicCoverImageUrl ?? undefined,
			coverGradient: 'grad-ai',
			chapterCount: idea.topicChapterCount ?? undefined,
			audioMinutes: idea.topicAudioMinutes ?? undefined,
			createdAt: idea.topicCreatedAt ?? idea.createdAt,
			isNewThisWeek: false,
			isFavorite: idea.isFavorite ?? false,
			voteValue: idea.voteValue ?? 0,
		} as TopicCardInput,
		{ showSubtitle: true, variant: 'mobile-compact' },
	);
}
